import React, { FormEvent, useCallback, useEffect, useMemo, useState } from "react";

import ImageDropzone from "../../components/common/ImageDropzone";
import Modal from "../../components/common/Modal";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import {
  createCompanyProfileBrand,
  createCompanyProfileCategory,
  fetchCompanyProfiles,
  fetchMinimumWage,
  setSelectedCategoryId,
  updateCompanyProfileBrand,
  updateCompanyProfileCategory,
  upsertMinimumWage,
} from "../../store/slices/appBasicSettingsSlice";
import { CompanyProfileBrand } from "../../types/admin";

const formatWageInput = (value: number | string) => {
  const digits = String(value).replace(/[^\d]/g, "");
  if (!digits) {
    return "";
  }
  return Number(digits).toLocaleString("ko-KR");
};

const parseWageInput = (value: string) => {
  const digits = value.replace(/[^\d]/g, "");
  return digits ? Number(digits) : 0;
};

const currentYear = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: 8 }, (_, index) => currentYear - 2 + index);

type BrandFormState = {
  brandName: string;
  sortOrder: string;
  logoFile: File | null;
  previewUrl: string | null;
};

const emptyBrandForm: BrandFormState = {
  brandName: "",
  sortOrder: "",
  logoFile: null,
  previewUrl: null,
};

const AppBasicSettingsPage = () => {
  const dispatch = useAppDispatch();
  const {
    currentWage,
    wageLoading,
    wageError,
    wageSaving,
    wageSaveError,
    categories,
    brandsByCategory,
    categoryLoading,
    categoryError,
    categorySaving,
    brandSaving,
    selectedCategoryId,
  } = useAppSelector((state) => state.appBasicSettings);

  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [wageInput, setWageInput] = useState("");
  const [categoryModalOpen, setCategoryModalOpen] = useState(false);
  const [categoryName, setCategoryName] = useState("");
  const [editingCategoryId, setEditingCategoryId] = useState<number | null>(null);
  const [brandModalOpen, setBrandModalOpen] = useState(false);
  const [editingBrandId, setEditingBrandId] = useState<number | null>(null);
  const [brandForm, setBrandForm] = useState<BrandFormState>(emptyBrandForm);

  const selectedCategory = useMemo(
    () => categories.find((item) => item.category_id === selectedCategoryId) || null,
    [categories, selectedCategoryId]
  );

  const brands = useMemo(
    () => (selectedCategoryId != null ? brandsByCategory[selectedCategoryId] || [] : []),
    [brandsByCategory, selectedCategoryId]
  );

  useEffect(() => {
    dispatch(fetchCompanyProfiles());
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchMinimumWage(selectedYear));
  }, [dispatch, selectedYear]);

  useEffect(() => {
    if (currentWage && currentWage.year === selectedYear) {
      setWageInput(formatWageInput(currentWage.hourly_wage));
    } else if (!wageLoading) {
      setWageInput("");
    }
  }, [currentWage, selectedYear, wageLoading]);

  useEffect(() => {
    return () => {
      if (brandForm.previewUrl?.startsWith("blob:")) {
        URL.revokeObjectURL(brandForm.previewUrl);
      }
    };
  }, [brandForm.previewUrl]);

  const closeCategoryModal = () => {
    setCategoryModalOpen(false);
    setEditingCategoryId(null);
    setCategoryName("");
  };

  const openCreateCategoryModal = () => {
    setEditingCategoryId(null);
    setCategoryName("");
    setCategoryModalOpen(true);
  };

  const openEditCategoryModal = () => {
    if (!selectedCategory) {
      return;
    }
    setEditingCategoryId(selectedCategory.category_id);
    setCategoryName(selectedCategory.name);
    setCategoryModalOpen(true);
  };

  const closeBrandModal = () => {
    if (brandForm.previewUrl?.startsWith("blob:")) {
      URL.revokeObjectURL(brandForm.previewUrl);
    }
    setBrandForm(emptyBrandForm);
    setEditingBrandId(null);
    setBrandModalOpen(false);
  };

  const setBrandLogoFile = useCallback((file: File | null) => {
    setBrandForm((prev) => {
      if (prev.previewUrl?.startsWith("blob:")) {
        URL.revokeObjectURL(prev.previewUrl);
      }
      return {
        ...prev,
        logoFile: file,
        previewUrl: file ? URL.createObjectURL(file) : null,
      };
    });
  }, []);

  const openCreateBrandModal = () => {
    if (brandForm.previewUrl?.startsWith("blob:")) {
      URL.revokeObjectURL(brandForm.previewUrl);
    }
    setEditingBrandId(null);
    setBrandForm({
      ...emptyBrandForm,
      sortOrder: String((brands[brands.length - 1]?.sort_order ?? brands.length) + 1),
    });
    setBrandModalOpen(true);
  };

  const openEditBrandModal = (brand: CompanyProfileBrand) => {
    if (brandForm.previewUrl?.startsWith("blob:")) {
      URL.revokeObjectURL(brandForm.previewUrl);
    }
    setEditingBrandId(brand.brand_id);
    setBrandForm({
      brandName: brand.brand_name,
      sortOrder: String(brand.sort_order),
      logoFile: null,
      previewUrl: brand.logo_url,
    });
    setBrandModalOpen(true);
  };

  const onSaveWage = async (event: FormEvent) => {
    event.preventDefault();
    const hourlyWage = parseWageInput(wageInput);
    if (!hourlyWage || hourlyWage <= 0) {
      window.alert("최저 시급을 올바르게 입력해 주세요.");
      return;
    }

    try {
      await dispatch(upsertMinimumWage({ year: selectedYear, hourly_wage: hourlyWage })).unwrap();
      window.alert("최저시급이 저장되었습니다.");
    } catch (error) {
      window.alert((error as string) || "최저시급 저장에 실패했습니다.");
    }
  };

  const onSubmitCategory = async (event: FormEvent) => {
    event.preventDefault();
    const name = categoryName.trim();
    if (!name) {
      window.alert("분류명을 입력해 주세요.");
      return;
    }

    try {
      if (editingCategoryId) {
        await dispatch(
          updateCompanyProfileCategory({ categoryId: editingCategoryId, name })
        ).unwrap();
        window.alert("분류가 수정되었습니다.");
      } else {
        await dispatch(createCompanyProfileCategory({ name })).unwrap();
        window.alert("분류가 추가되었습니다.");
      }
      closeCategoryModal();
      dispatch(fetchCompanyProfiles());
    } catch (error) {
      window.alert((error as string) || "분류 저장에 실패했습니다.");
    }
  };

  const onSubmitBrand = async (event: FormEvent) => {
    event.preventDefault();
    if (!selectedCategoryId) {
      window.alert("분류를 먼저 선택해 주세요.");
      return;
    }

    const brandName = brandForm.brandName.trim();
    if (!brandName) {
      window.alert("브랜드명을 입력해 주세요.");
      return;
    }

    if (!editingBrandId && !brandForm.logoFile) {
      window.alert("로고 이미지를 선택해 주세요.");
      return;
    }

    const formData = new FormData();
    formData.append("brand_name", brandName);
    if (brandForm.sortOrder.trim()) {
      formData.append("sort_order", String(Number(brandForm.sortOrder) || 0));
    }
    if (brandForm.logoFile) {
      formData.append("logo", brandForm.logoFile);
    }

    try {
      if (editingBrandId) {
        await dispatch(updateCompanyProfileBrand({ brandId: editingBrandId, formData })).unwrap();
        window.alert("브랜드 로고가 수정되었습니다.");
      } else {
        await dispatch(createCompanyProfileBrand({ categoryId: selectedCategoryId, formData })).unwrap();
        window.alert("브랜드 로고가 추가되었습니다.");
      }
      closeBrandModal();
      dispatch(fetchCompanyProfiles());
    } catch (error) {
      window.alert((error as string) || "브랜드 저장에 실패했습니다.");
    }
  };

  return (
    <section className="admin-page app-basic-settings-page">
      <div className="app-basic-card">
        <h2 className="app-basic-card__title">법정 최저시급 기준 설정</h2>
        <p className="app-basic-card__desc">
          구인공고 및 근로계약서 작성 시 &apos;최저시급 미만 입력 방지&apos; 검증 로직에 즉시 적용됩니다.
        </p>

        <form className="app-basic-wage-row" onSubmit={onSaveWage}>
          <div className="app-basic-wage-fields">
            <label className="app-basic-field">
              <span>적용연도</span>
              <div className="app-basic-control">
                <select value={selectedYear} onChange={(e) => setSelectedYear(Number(e.target.value))}>
                  {YEAR_OPTIONS.map((year) => (
                    <option key={year} value={year}>
                      {year}년
                    </option>
                  ))}
                </select>
              </div>
            </label>

            <label className="app-basic-field">
              <span>최저 시급</span>
              <div className="app-basic-control">
                <input
                  type="text"
                  inputMode="numeric"
                  value={wageInput}
                  onChange={(e) => setWageInput(formatWageInput(e.target.value))}
                  placeholder="10,700"
                />
                <em className="app-basic-control__unit">원</em>
              </div>
            </label>
          </div>

          <button className="app-basic-save-btn" type="submit" disabled={wageSaving}>
            {wageSaving ? "저장 중..." : "시급 저장"}
          </button>
        </form>

        {wageLoading && <p className="loading-text">최저시급 정보를 불러오는 중입니다...</p>}
        {wageError && !wageLoading && <p className="error-text">{wageError}</p>}
        {wageSaveError && <p className="error-text">{wageSaveError}</p>}
      </div>

      <div className="app-basic-card app-basic-card--profile">
        <h2 className="app-basic-card__title">기본 업체 프로필 설정</h2>
        <p className="app-basic-card__desc muted">앱 내 구인공고에 올라갈 기본 업체 프로필 선택에 적용됩니다.</p>

        <div className="app-basic-profile-layout">
          <div className="app-basic-category-panel">
            <div className="app-basic-panel-head">
              <h3>1단계 대분류(카테고리)</h3>
              <button className="app-basic-outline-btn" type="button" onClick={openCreateCategoryModal}>
                <span className="app-basic-outline-btn__plus" aria-hidden>
                  +
                </span>
                분류 추가
              </button>
            </div>

            <div className="app-basic-category-list">
              {categories.map((category) => {
                const selected = category.category_id === selectedCategoryId;
                return (
                  <button
                    key={category.category_id}
                    type="button"
                    className={`app-basic-category-item ${selected ? "is-selected" : ""}`}
                    onClick={() => dispatch(setSelectedCategoryId(category.category_id))}
                  >
                    <span className="app-basic-category-item__name">{category.name}</span>
                    <span className="app-basic-category-item__meta">
                      {category.brand_count}개
                      <span className="app-basic-category-item__chevron" aria-hidden>
                        ›
                      </span>
                    </span>
                  </button>
                );
              })}
              {!categoryLoading && categories.length === 0 && (
                <p className="app-basic-empty">등록된 분류가 없습니다.</p>
              )}
            </div>

            {selectedCategory && (
              <div className="app-basic-category-actions">
                <button className="table-action-btn" type="button" onClick={openEditCategoryModal}>
                  분류 수정
                </button>
              </div>
            )}

            {categoryLoading && <p className="loading-text">분류를 불러오는 중입니다...</p>}
            {categoryError && <p className="error-text">{categoryError}</p>}
          </div>

          <div className="app-basic-brand-panel">
            <div className="app-basic-panel-head">
              <h3>
                {selectedCategory
                  ? `[${selectedCategory.name}] 하위 브랜드 목록`
                  : "하위 브랜드 목록"}
              </h3>
              <button
                className="app-basic-outline-btn"
                type="button"
                onClick={openCreateBrandModal}
                disabled={!selectedCategoryId}
              >
                <span className="app-basic-outline-btn__plus" aria-hidden>
                  +
                </span>
                브랜드 로고 추가
              </button>
            </div>

            <div className="table-shell app-basic-brand-table-shell">
              <table className="admin-table app-basic-brand-table">
                <colgroup>
                  <col className="app-basic-brand-col--order" />
                  <col className="app-basic-brand-col--logo" />
                  <col className="app-basic-brand-col--name" />
                  <col className="app-basic-brand-col--action" />
                </colgroup>
                <thead>
                  <tr>
                    <th>순서</th>
                    <th>로고 이미지</th>
                    <th>브랜드명</th>
                    <th>관리</th>
                  </tr>
                </thead>
                <tbody>
                  {brands.map((brand, index) => (
                    <tr key={brand.brand_id}>
                      <td>{brand.sort_order || index + 1}</td>
                      <td>
                        <div className="app-basic-logo-thumb">
                          <img src={brand.logo_url} alt={brand.brand_name} />
                        </div>
                      </td>
                      <td>{brand.brand_name}</td>
                      <td>
                        <div className="table-actions">
                          <button
                            className="app-basic-edit-btn"
                            type="button"
                            onClick={() => openEditBrandModal(brand)}
                          >
                            수정
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {!categoryLoading && selectedCategoryId != null && brands.length === 0 && (
                    <tr>
                      <td className="empty-row" colSpan={4}>
                        등록된 브랜드 로고가 없습니다.
                      </td>
                    </tr>
                  )}
                  {selectedCategoryId == null && (
                    <tr>
                      <td className="empty-row" colSpan={4}>
                        왼쪽에서 분류를 선택해 주세요.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <Modal
        open={categoryModalOpen}
        title={editingCategoryId ? "분류 수정" : "분류 추가"}
        description="구인공고 업체 프로필의 1단계 대분류를 관리합니다."
        onClose={closeCategoryModal}
      >
        <form className="admin-modal-form" onSubmit={onSubmitCategory}>
          <label className="admin-modal-field">
            <span>분류명</span>
            <input
              value={categoryName}
              onChange={(e) => setCategoryName(e.target.value)}
              placeholder="예: 편의점 브랜드 기본 로고"
              autoFocus
            />
          </label>
          <div className="admin-modal-actions">
            <button className="admin-modal-primary" type="submit" disabled={categorySaving}>
              {categorySaving ? "저장 중..." : editingCategoryId ? "수정" : "추가"}
            </button>
            <button className="admin-modal-secondary" type="button" onClick={closeCategoryModal}>
              취소
            </button>
          </div>
        </form>
      </Modal>

      <Modal
        open={brandModalOpen}
        title={editingBrandId ? "브랜드 로고 수정" : "브랜드 로고 추가"}
        description="드래그, 클릭, 또는 Ctrl/⌘ + V 붙여넣기로 로고를 넣을 수 있습니다."
        onClose={closeBrandModal}
        width={520}
      >
        <form className="admin-modal-form" onSubmit={onSubmitBrand}>
          <div className="admin-modal-grid">
            <label className="admin-modal-field">
              <span>브랜드명</span>
              <input
                type="text"
                value={brandForm.brandName}
                onChange={(e) => setBrandForm((prev) => ({ ...prev, brandName: e.target.value }))}
                placeholder="예: CU"
                autoFocus
              />
            </label>
            <label className="admin-modal-field">
              <span>순서</span>
              <input
                type="number"
                min={1}
                value={brandForm.sortOrder}
                onChange={(e) => setBrandForm((prev) => ({ ...prev, sortOrder: e.target.value }))}
              />
            </label>
          </div>

          <div className="admin-modal-field">
            <span>로고 이미지{editingBrandId ? " (변경 시에만 선택)" : ""}</span>
            <ImageDropzone
              enabled={brandModalOpen}
              previewUrl={brandForm.previewUrl}
              fileName={brandForm.logoFile?.name}
              onSelect={setBrandLogoFile}
              onClear={
                editingBrandId
                  ? undefined
                  : () => {
                      setBrandLogoFile(null);
                    }
              }
            />
          </div>

          <div className="admin-modal-actions">
            <button className="admin-modal-primary" type="submit" disabled={brandSaving}>
              {brandSaving ? "저장 중..." : editingBrandId ? "수정" : "추가"}
            </button>
            <button className="admin-modal-secondary" type="button" onClick={closeBrandModal}>
              취소
            </button>
          </div>
        </form>
      </Modal>
    </section>
  );
};

export default AppBasicSettingsPage;
